import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import type { User } from '@/models'
import { loginFulfilled, logoutFulfilled } from '@/services/auth'
import { cleanLoggedInUser, redirectToLogin } from '@/utils/auth'

export interface AuthState {
  authenticated: boolean
  token: string | undefined
  user: User | null
}

const initialState: AuthState = {
  authenticated: false,
  token: undefined,
  user: null,
}

interface SetUserPayload {
  token?: string
  data?: User
}

interface LogoutPayload {
  forceLogin?: boolean
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(
      state,
      { payload: { token, data } }: PayloadAction<SetUserPayload>
    ) {
      state.authenticated = !!token
      state.token = token
      state.user = data ?? null
    },
    logout(
      state,
      { payload: { forceLogin } = {} }: PayloadAction<LogoutPayload>
    ) {
      state.authenticated = false
      state.token = undefined
      state.user = null
      cleanLoggedInUser()
      if (forceLogin) redirectToLogin()
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(loginFulfilled),
      (state, { payload: { data, token } }) => {
        state.authenticated = !!token
        state.token = token
        state.user = data ?? null
      }
    )
    builder.addMatcher(logoutFulfilled, () => initialState)
  },
})

const persistConfig = {
  key: 'auth',
  storage,
  whitelist: ['authenticated', 'token', 'user'],
}

export const { setUser, logout } = authSlice.actions

export default persistReducer(persistConfig, authSlice.reducer)
